import React, { useState } from "react";
import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const QUESTION_TYPES = [
  { key: "text", label: "Short text" }, { key: "longtext", label: "Long text" }, { key: "select", label: "Single choice" },
  { key: "multiselect", label: "Multiple choice" }, { key: "date", label: "Date" }, { key: "number", label: "Number" },
  { key: "yesno", label: "Yes / No" },
];
export const typeLabel = (k: string) => QUESTION_TYPES.find((t) => t.key === k)?.label || k;
export const blankField = () => ({ id: Math.random().toString(36).slice(2, 10), label: "New question", type: "text", options: [], required: false, help: "" });

function FieldEditor({ f, i, n, onChange, onMove, onRemove, prefix }: any) {
  const [opts, setOpts] = useState((f.options || []).join(", "));
  const tid = `${prefix}-field`;
  return (
    <div className="space-y-2 rounded-xl border border-border p-3" data-testid={`${tid}-${i}`}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px]">
        <Input value={f.label} onChange={(e) => onChange({ ...f, label: e.target.value })} data-testid={`${tid}-label-${i}`} />
        <Select value={f.type} onValueChange={(v) => onChange({ ...f, type: v })}>
          <SelectTrigger data-testid={`${tid}-type-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{QUESTION_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {(f.type === "select" || f.type === "multiselect") && (
        <Input value={opts} placeholder="Options, comma separated" onChange={(e) => { setOpts(e.target.value); onChange({ ...f, options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) }); }} data-testid={`${tid}-options-${i}`} />
      )}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={f.required} onCheckedChange={(v) => onChange({ ...f, required: v })} data-testid={`${tid}-required-${i}`} /> Required
        </label>
        <div className="flex">
          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => onMove(-1)} data-testid={`${tid}-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => onMove(1)} data-testid={`${tid}-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`${tid}-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

export function QuestionsEditor({ fields, onChange, prefix = "form" }: { fields: any[]; onChange: (f: any[]) => void; prefix?: string }) {
  const move = (i: number, d: number) => {
    const next = [...fields];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {fields.map((f, i) => (
        <FieldEditor key={f.id} f={f} i={i} n={fields.length} prefix={prefix} onChange={(nf: any) => onChange(fields.map((x, j) => (j === i ? nf : x)))}
          onMove={(d: number) => move(i, d)} onRemove={() => onChange(fields.filter((_, j) => j !== i))} />
      ))}
      <Button variant="secondary" onClick={() => onChange([...fields, blankField()])} data-testid={`${prefix}-add-field-button`}><Plus className="mr-1 h-4 w-4" /> Add question</Button>
    </div>
  );
}

export function LockedQuestions({ eventType }: { eventType: any }) {
  return (
    <div className="space-y-2" data-testid="locked-base-questions">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary"><Lock className="h-3.5 w-3.5" /> ExpoEventHub standard · {eventType.name}</p>
      {eventType.questions.map((q: any) => (
        <div key={q.id} className="flex items-center gap-2 rounded-xl border border-dashed border-border bg-muted/40 px-3 py-2 text-sm" data-testid={`locked-question-${q.id}`}>
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">{q.label}{q.required && <span className="text-rose-600"> *</span>}</span>
          <span className="text-xs text-muted-foreground">{typeLabel(q.type)}</span>
        </div>
      ))}
      {eventType.questions.length === 0 && <p className="text-xs text-muted-foreground">No standard questions for this type yet.</p>}
    </div>
  );
}
